import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./login.css";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  // Handle redirect after successful login (page refresh safe)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const justLoggedIn = localStorage.getItem("justLoggedIn");

    if (token && justLoggedIn === "true") {
      localStorage.removeItem("justLoggedIn");
      navigate("/add-book"); // Redirect to dashboard / add-book
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      const response = await fetch(
        "https://panafricanbookexchange-wandererspath.com/api/auth/login-user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim(), password }),
        }
      );

      const raw = await response.text();
      console.log("RAW RESPONSE FROM SERVER:", raw);

      // Backend returned HTML (500/404 server error page) instead of JSON
      if (raw.startsWith("<!DOCTYPE") || raw.startsWith("<html")) {
        setErrorMessage("Server error: Backend returned an invalid HTML response.");
        setLoading(false);
        return;
      }

      let data;
      try {
        data = JSON.parse(raw);
      } catch (err) {
        console.error("JSON Parse Error:", err);
        setErrorMessage("Invalid response format received from server.");
        setLoading(false);
        return;
      }

      setLoading(false);

      if (data.status === "ok" && data.token) {
        const decoded = jwtDecode(data.token);

        // Normalize role safely
        const userType = (decoded.role || decoded.userType || "author").toLowerCase();
        const userEmail = decoded.email;
        const userId = decoded._id || decoded.userId;

        // Persist session
        localStorage.setItem("token", data.token);
        localStorage.setItem("userType", userType);
        localStorage.setItem("userEmail", userEmail);
        localStorage.setItem("userId", userId);
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem(
          "user",
          JSON.stringify({
            _id: userId,
            email: userEmail,
            role: userType,
          })
        );
        localStorage.setItem("justLoggedIn", "true");

        // Redirect to Add Book page
        navigate("/add-book");
      } else {
        // Display backend error message directly in UI
        setErrorMessage(data.error || data.message || "Login failed. Please verify credentials.");
      }
    } catch (error) {
      console.error("Login Error:", error);
      setErrorMessage("Network error: Unable to connect to backend server.");
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-header">
          <h2>Author Portal</h2>
          <p>Sign in to manage your books and publisher dashboard</p>
        </div>

        {errorMessage && (
          <div className="login-alert" role="alert">
            <span className="alert-icon">??</span>
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              className="form-input"
              placeholder="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <div className="label-row">
              <label htmlFor="password">Password</label>
              <Link to="/forgot-password" className="inline-link text-small">
                Forgot password?
              </Link>
            </div>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`btn-submit ${loading ? "is-loading" : ""}`}
          >
            {loading ? (
              <span className="button-loader-content">
                <span className="spinner"></span> Logging in...
              </span>
            ) : (
              "Sign In"
            )}
          </button>

          <div className="login-footer">
            <p>
              Don't have an author account?{" "}
              <Link to="/registerAdmin" className="inline-link font-semibold">
                Register here
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}